/**
 * The basket, held in the browser.
 *
 * Deliberately client-side and anonymous. A shopper who has not registered yet still has a
 * basket -- that is the whole point of the storefront funnel -- and a server-side cart
 * would need a row keyed by something we do not have until they sign up. The basket becomes
 * server state exactly once, at `POST /marketplace/orders`, and from that moment the order
 * is the record.
 *
 * The stored line carries a price, and that price is **for display only**. Nothing here is
 * sent to the API: `placeStorefrontOrder` posts ids and quantities, and the server prices
 * the order with the same code that prices a rep's order. A stale price in `localStorage`
 * therefore shows the shopper a wrong subtotal until the page refetches, which is a
 * cosmetic problem; it can never become a wrong charge.
 *
 * Persisted by hand rather than with `zustand/middleware/persist`, for the same reason
 * `session.ts` writes `localStorage` itself: every access is wrapped, because storage
 * throws outright in private-mode Safari and under enterprise policy, and a basket that
 * cannot be saved must still work for the length of the visit.
 */

import { create } from 'zustand'

import { shopPrice, type StorefrontProduct } from '@/features/marketplace/api'

const STORAGE_KEY = 'fmcg.cart'

/** Matches `CheckoutItemInput.quantity` on the API (`gt=0, le=10_000`). */
export const MAX_QUANTITY = 10_000

export interface CartLine {
  product_id: string
  code: string
  name: string
  /** Snapshot for rendering the basket. Never sent; see the module docstring. */
  price: string
  /** Percent, e.g. `"18.000"`. Only used to estimate tax on the basket screen. */
  tax_rate: string
  unit_of_measure: string
  image: string | null
  quantity: number
}

interface CartState {
  lines: CartLine[]
  /** Adds, or tops up a line that is already there. Returns the resulting quantity. */
  add: (product: StorefrontProduct, quantity?: number) => number
  setQuantity: (productId: string, quantity: number) => void
  remove: (productId: string) => void
  clear: () => void
}

function read(): CartLine[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    // Written by an older build, or by hand. Anything that does not have the two fields
    // the checkout actually needs is dropped rather than repaired.
    return parsed.filter(
      (line): line is CartLine =>
        typeof line === 'object' &&
        line !== null &&
        typeof (line as CartLine).product_id === 'string' &&
        Number.isFinite((line as CartLine).quantity) &&
        (line as CartLine).quantity > 0,
    )
  } catch {
    return []
  }
}

function write(lines: CartLine[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lines))
  } catch {
    /* private mode or storage policy -- the basket lives for this visit only */
  }
}

/** Clamps into the range the API will accept, so the error arrives at the button and not
 *  as a 422 three screens later. */
const clamp = (quantity: number) => Math.max(1, Math.min(MAX_QUANTITY, Math.floor(quantity)))

export const useCart = create<CartState>((set, get) => ({
  lines: read(),

  add: (product, quantity = 1) => {
    const existing = get().lines.find((line) => line.product_id === product.id)
    const next = clamp((existing?.quantity ?? 0) + quantity)
    const lines = existing
      ? get().lines.map((line) =>
          // The price is refreshed too: a sale may have started or ended since it went in.
          line.product_id === product.id
            ? { ...line, quantity: next, price: shopPrice(product) }
            : line,
        )
      : [
          ...get().lines,
          {
            product_id: product.id,
            code: product.code,
            name: product.name,
            price: shopPrice(product),
            tax_rate: product.tax_rate,
            unit_of_measure: product.unit_of_measure,
            image: product.images?.[0] ?? null,
            quantity: next,
          },
        ]
    write(lines)
    set({ lines })
    return next
  },

  setQuantity: (productId, quantity) => {
    if (quantity < 1) {
      get().remove(productId)
      return
    }
    const lines = get().lines.map((line) =>
      line.product_id === productId ? { ...line, quantity: clamp(quantity) } : line,
    )
    write(lines)
    set({ lines })
  },

  remove: (productId) => {
    const lines = get().lines.filter((line) => line.product_id !== productId)
    write(lines)
    set({ lines })
  },

  clear: () => {
    write([])
    set({ lines: [] })
  },
}))

/** Total number of units, for the header badge. */
export const cartCount = (lines: CartLine[]) =>
  lines.reduce((total, line) => total + line.quantity, 0)

/**
 * The basket's goods total, as a number.
 *
 * Floating point is acceptable *here* and nowhere else: this figure is rendered next to a
 * "calculated at checkout" caveat and is never sent anywhere. Every amount that binds --
 * the order's subtotal, tax, delivery and `amount_due` -- is a `Decimal` computed on the
 * server and arrives as a string.
 */
export const cartSubtotal = (lines: CartLine[]) =>
  lines.reduce((total, line) => total + Number(line.price) * line.quantity, 0)

/**
 * Estimated tax on the basket, at each line's own rate.
 *
 * An estimate in the strict sense: the server applies customer discounts, per-line rounding
 * and the jurisdiction's split (CGST/SGST or IGST) that this cannot know about, and the
 * basket screen labels the figure accordingly. It exists so that the goods total shown
 * before checkout is in the same ballpark as the one on the order, rather than short by
 * eighteen percent.
 */
export const cartTax = (lines: CartLine[]) =>
  lines.reduce(
    (total, line) =>
      total + (Number(line.price) * line.quantity * (Number(line.tax_rate) || 0)) / 100,
    0,
  )
